import { useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import {
  saveDoubleUpUsedGameweeks,
  saveCleanSweepUsedGameweeks,
  saveGoalfestUsedGameweeks,
} from '../storage'
import { playChip, cancelChip } from '../chipStatus'
import { computeGoalfestBonus } from '../season'
import { useAutosave } from './usePersistedState'
import type { GameweekDetail, GameweekResult } from '../types'

export interface UseChipsOptions {
  currentGameweek: number
  /** Playing and cancelling are both closed off once the shared deadline has passed. */
  isDeadlinePassed: boolean
  isLoading: boolean
  onToast: (message: string) => void
  /** Persisted per-gameweek details, used to freeze each played chip's final bonus. */
  seasonDetails: Record<number, GameweekDetail>
  seasonHistory: GameweekResult[]
  /** Gameweeks whose results are all in — the only ones a "final" chip number may be read from. */
  settledGameweeks: number[]
}

export interface ChipsController {
  doubleUpUsedGameweeks: number[]
  setDoubleUpUsedGameweeks: Dispatch<SetStateAction<number[]>>
  cleanSweepUsedGameweeks: number[]
  setCleanSweepUsedGameweeks: Dispatch<SetStateAction<number[]>>
  goalfestUsedGameweeks: number[]
  setGoalfestUsedGameweeks: Dispatch<SetStateAction<number[]>>
  isDoubleUpActive: boolean
  isCleanSweepActive: boolean
  isGoalfestActive: boolean
  playDoubleUp: () => void
  cancelDoubleUp: () => void
  playCleanSweep: () => void
  cancelCleanSweep: () => void
  playGoalfest: () => void
  cancelGoalfest: () => void
  /** Frozen final Goalfest bonus per gameweek it was played and finalized. */
  goalfestFinalBonusByGameweek: Record<number, number>
  /** Frozen final Clean Sweep total per gameweek it was played and finalized. */
  cleanSweepFinalTotalByGameweek: Record<number, number>
}

/**
 * The three chips are one shape repeated: a list of the gameweeks each has
 * been played in, played/cancelled through the shared deadline gating in `chipStatus`, autosaved
 * as a whole list.
 *
 * The two "final" maps are derived from persisted data rather than stored separately, so there is
 * nothing extra to keep in sync: Goalfest recomputes its bonus from that gameweek's stored detail,
 * and Clean Sweep fully replaces the gameweek's scoring, so the finalized
 * `GameweekResult.points` already *is* its total, 0 included.
 */
export function useChips({
  currentGameweek,
  isDeadlinePassed,
  isLoading,
  onToast,
  seasonDetails,
  seasonHistory,
  settledGameweeks,
}: UseChipsOptions): ChipsController {
  const [doubleUpUsedGameweeks, setDoubleUpUsedGameweeks] = useState<number[]>([])
  const [cleanSweepUsedGameweeks, setCleanSweepUsedGameweeks] = useState<number[]>([])
  const [goalfestUsedGameweeks, setGoalfestUsedGameweeks] = useState<number[]>([])

  useAutosave({
    value: doubleUpUsedGameweeks,
    enabled: !isLoading,
    save: saveDoubleUpUsedGameweeks,
    onError: () => onToast('Failed to save Double Up.'),
  })
  useAutosave({
    value: cleanSweepUsedGameweeks,
    enabled: !isLoading,
    save: saveCleanSweepUsedGameweeks,
    onError: () => onToast('Failed to save Clean Sweep.'),
  })
  useAutosave({
    value: goalfestUsedGameweeks,
    enabled: !isLoading,
    save: saveGoalfestUsedGameweeks,
    onError: () => onToast('Failed to save Goalfest.'),
  })

  const play = (set: Dispatch<SetStateAction<number[]>>) => () =>
    set((prev) => playChip(prev, currentGameweek, isDeadlinePassed))
  const cancel = (set: Dispatch<SetStateAction<number[]>>) => () =>
    set((prev) => cancelChip(prev, currentGameweek, isDeadlinePassed))

  /**
   * A chip's number is only "final" once the gameweek is over.
   *
   * Goalfest's bonus and Clean Sweep's total both grow through the afternoon as goals land — that
   * is the honest live reading, and it is what the grid's live ticker shows. What must not happen
   * is the *frozen* label ("GW3 · +7") being taken from a gameweek that is still being played: the
   * server writes a scored row and a stored detail from the first full-time whistle onwards, so
   * their presence stopped meaning "this gameweek is done" the moment scoring became continuous.
   * Until then the chip simply reads as used, without a number.
   */
  const isSettled = (gameweek: number) => settledGameweeks.includes(gameweek)

  const goalfestFinalBonusByGameweek: Record<number, number> = {}
  for (const gameweek of goalfestUsedGameweeks) {
    const detail = isSettled(gameweek) ? seasonDetails[gameweek] : undefined
    if (detail) {
      goalfestFinalBonusByGameweek[gameweek] = computeGoalfestBonus(
        detail.games,
        detail.predictions,
        detail.results
      )
    }
  }

  const cleanSweepFinalTotalByGameweek: Record<number, number> = {}
  for (const gameweek of cleanSweepUsedGameweeks) {
    const result = isSettled(gameweek)
      ? seasonHistory.find((r) => r.gameweek === gameweek)
      : undefined
    if (result) {
      cleanSweepFinalTotalByGameweek[gameweek] = result.points
    }
  }

  return {
    doubleUpUsedGameweeks,
    setDoubleUpUsedGameweeks,
    cleanSweepUsedGameweeks,
    setCleanSweepUsedGameweeks,
    goalfestUsedGameweeks,
    setGoalfestUsedGameweeks,
    isDoubleUpActive: doubleUpUsedGameweeks.includes(currentGameweek),
    isCleanSweepActive: cleanSweepUsedGameweeks.includes(currentGameweek),
    isGoalfestActive: goalfestUsedGameweeks.includes(currentGameweek),
    playDoubleUp: play(setDoubleUpUsedGameweeks),
    cancelDoubleUp: cancel(setDoubleUpUsedGameweeks),
    playCleanSweep: play(setCleanSweepUsedGameweeks),
    cancelCleanSweep: cancel(setCleanSweepUsedGameweeks),
    playGoalfest: play(setGoalfestUsedGameweeks),
    cancelGoalfest: cancel(setGoalfestUsedGameweeks),
    goalfestFinalBonusByGameweek,
    cleanSweepFinalTotalByGameweek,
  }
}
